import { CheckCircleOutline } from '@mui/icons-material';
import {
  Button,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../hooks/NotificationContext';
import { resendLinkToUser } from '../../lib/utils';
import { createUser } from '../../modules/users';
import SocialLogins from '../Login/SocialLogins';
import OrganizationForm from './OrganizationForm';
import RegisterForm from './RegisterForm';

const RegistrationConfirm = ({ handleGoHome, resendLink }) => (
  <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
    <CheckCircleOutline
      color="success"
      sx={{ fontSize: 80 }}
      aria-label="Registration Success"
    />
    <Typography variant="h4" sx={{ mt: 3, mb: 2 }}>
      Registration Successful!
    </Typography>
    <Typography variant="body1" sx={{ mb: 4 }}>
      We've sent a confirmation email to your inbox. Please check your email and
      follow the instructions to verify your account. If you didn't receive the
      email, you can request it again below.
    </Typography>
    <Stack spacing={2} direction="row" justifyContent="center">
      <Button
        variant="contained"
        color="primary"
        onClick={handleGoHome}
        sx={{ minWidth: 150 }}>
        Go to Home
      </Button>
      <Button variant="outlined" onClick={resendLink}>
        Resend Email
      </Button>
    </Stack>
  </Container>
);

function RegisterUser() {
  const [isStepOneCompleted, setIsStepOneCompleted] = useState(false);
  const [userDetails, setUserDetails] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { addNotification } = useNotification();
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const isTokenValid = Date.now() <= tokenData.exp * 1000;
      if (isTokenValid) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, []);
  // Step 1 (Google login or Email/Password)
  const handleStepOne = async (values) => {
    const user = {
      ...values,
      platform: values.platform || 'web',
      role: 'org-admin',
      terms_conditions: true,
    };

    setUserDetails(user);
    setIsStepOneCompleted(true);
  };

  // Step 2 Organization
  const handleStepTwo = async (values) => {
  if (!values.organization?.trim()) {
    addNotification('error', 'Organization name is required. Please try again');
    return;
  }
  if (!values.website?.trim()) {
    addNotification('error', 'Website is required. Please try again');
    return;
  }

  const completeUserDetails = {
    ...userDetails,
    organization:       values.organization.trim(),
    industry:           values.industry,
    org_phone_number:  values.orgPhoneNumber   || null,
    org_country_code:  values.orgCountryCode   || null, 
    website:            values.website.trim(),
    linkedin:           values.linkedin?.trim() || null,
    evaluation_reason:  values.evaluationReason.trim(),
  };

  try {
    await createUser(completeUserDetails);
    setShowConfirmation(true);
  } catch (error) {
    addNotification('error', error.message || 'User registration failed. Please try again');
  }
};

  // Go back to Step 1
  const goBackToStepOne = () => {
    setIsStepOneCompleted(false);
    setShowConfirmation(false);
  };

  const handleResendLink = async () => {
    try {
      const response = await resendLinkToUser(
        userDetails?.email,
        'confirmation'
      );
      addNotification('success', response);
    } catch (error) {
      addNotification('error', error.message);
    }
  };

  if (showConfirmation) {
    return (
      <RegistrationConfirm
        handleGoHome={handleGoHome}
        resendLink={handleResendLink}
      />
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {!isStepOneCompleted ? (
          <>
            <Grid item xs={12}>
              <Stack
                direction="column"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ mb: { xs: -0.5, sm: 0.5 } }}>
                <Typography variant="h4">Create Account</Typography>
                <Typography variant="body1" className="subheading-text">
                  Join the GAINSS Revolution: Simplified Test Case Management
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <RegisterForm
                handleSubmit={handleStepOne}
                initialValues={userDetails}
              />{' '}
              {/* Step 1 Form */}
            </Grid>

            <Grid item xs={12}>
              <Divider>
                <Typography variant="body2">Or</Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <SocialLogins handleSubmit={handleStepOne} />
            </Grid>
          </>
        ) : (
          <>
            {/* Step 2: Organization Form */}
            <Grid item xs={12}>
              <OrganizationForm handleSubmit={handleStepTwo} goBackToStepOne={goBackToStepOne} />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

export default RegisterUser;
